import {
  ChangeDetectionStrategy, ChangeDetectorRef, Component,
  Input, OnInit, OnDestroy,
} from '@angular/core';
import { Observable, of } from 'rxjs';
import { UntypedFormGroup } from '@angular/forms';
import * as _ from 'lodash';

import { TableEditableComponent } from 'shared/table/table-editable/table-editable.component';
import { InformationPageService } from '../information-page/information-page.service';
import * as utils from 'utils/utils';
import LanguageCodeMapper from 'utils/language-code-mapper';
import {takeWhile} from 'rxjs/operators';

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BaseGlobalSegmentComponent implements OnInit, OnDestroy {
  name: string = '';
  data: any;
  sourceData: any;
  segmentData: any[] = [];

  protected isPageActive: boolean = true;
  protected saveInProgress: boolean = false;
  protected formComponent: { form: UntypedFormGroup, tableEditable: TableEditableComponent } | void;

  /*******************************************
   * COMPONENT GETTERS
   *******************************************/

  get form(): UntypedFormGroup | void {
    return this.formComponent && this.formComponent.form;
  }

  get tableEditable(): TableEditableComponent | void {
    return this.formComponent && this.formComponent.tableEditable;
  }

  /*
    redefine this method in segment
    if you have restrictions for saving data
  */
  get isAllowedToSave(): boolean {
    return true;
  }

  // getter for checking whether form is changed
  get isDirty(): boolean {
    return this.form ? this.form.dirty : false;
  }

  get saveDisabled(): boolean {
    return this.form ? this.form.invalid : true;
  }

  get isValid(): boolean {
    return this.form ? !this.form.invalid : true;
  }

  // getter for checking whether table-editable is in editing status
  get isEditing(): boolean {
    return this.tableEditable ? this.tableEditable.isEditing : false;
  }
  // show action panel when form is changed
  get showActionPanel(): boolean {
    return this.isDirty;
  }

  constructor(
    protected informationPageService: InformationPageService,
    protected ref: ChangeDetectorRef,
  ) {}

  /*******************************************
   * COMPONENT HOOKS
   *******************************************/

  ngOnInit() {
    this.informationPageService.saveModalOpen
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((event: any) => {
      this.onSaveModalOpen(event);
    });
    this.informationPageService.save
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((event: any) => {
      this.onSave(event);
    });
    this.informationPageService.cancel
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((event: any) => {
      this.onCancel(event);
    });
  }

  ngOnDestroy() {
    this.isPageActive = false;
  }

  /*******************************************
   * COMPONENT PUBLIC METHODS
   *******************************************/

  hasError(field: any, formGroup: UntypedFormGroup): boolean {
    return utils.hasError(field, formGroup);
  }

  putFormData(publish: boolean= false): Observable<any> {
    // replace method with put service call
    console.warn('Not implemented');
    return of();
  }

  fetchFormData(): Observable<any> {
    // replace method with fetch service call
    console.warn('Not implemented');
    return of();
  }

  refresh(): void {
    this.refreshSegmentData();
    this.showLoader();
    this.refreshData().subscribe((data: any) => {
      this.setData(data);
      this.hideLoader();
    });
  }


  setData(data: any) {
    if (Array.isArray(data)) {
      this.data = [...data];
      this.sourceData = [...this.data];
    } else {
      this.data = {...data};
      this.sourceData = {...data};
    }
    this.ref.markForCheck();
  }

  /* eslint-disable no-empty,@typescript-eslint/no-empty-function */
  save() {}
  /* eslint-enable no-empty, @typescript-eslint/no-empty-function */

  /*******************************************
   * COMPONENT HANDLERS
   *******************************************/

  /*
    event: {
      currentSegment: string, // segment name
      isSaveBeforeLeaving: boolean,
      updateItems: boolean,
    }
  */

  onSave(event: any) {
    /*
    if ((<any>this.formComponent).isInValid) {
      return console.log('Form is invalid');
    }
    */
    // save after changing route with save before leaving modal
    if (event.isSaveBeforeLeaving) {
      this.putFormData().subscribe(() => {
        this.onSaved(event);
      });
      _.defer(() => this.informationPageService.setCanDeactivate(true));
      return;
    }
    if (this.saveInProgress) { return; }
    this.saveInProgress = true;
    this.putFormData(_.get(event, 'updateItems', false))
    .subscribe(() => {
      this.fetchFormData()
      .subscribe((data: any) => {
        this.saveInProgress = false;
        this.setData(data);
        this.onSaved(event);
        if (this.form) { this.form.markAsPristine(); }
      });
      // emit that data was saved for informationPageService
      // update segments validation
      this.informationPageService.emitSaved();
    }, (err: any) => this.saveInProgress = false);
  }

  onSaveModalOpen(event: any) {
    this.informationPageService.setItemsCount(1235);
  }

  onCancel(event: any) {
    if (Array.isArray(this.sourceData)) {
      this.data = [...this.sourceData];
    } else {
      this.data = {...this.sourceData};
    }
    if (this.form) { this.form.markAsPristine(); }
    this.ref.markForCheck();
  }

  /*
    redefine this method in segment
    if you want to do smth on successfull data save
    event: {
      currentSegment: string, // segment name
      isSaveBeforeLeaving: boolean,
      updateItems: boolean,
    }
  */
  onSaved(event: any) {
    console.log('You have not defined method onSaved!');
  }

  /*******************************************
   * COMPONENT PROTECTED METHODS
   *******************************************/

  // inherit this methods in your segment component
  protected refreshSegmentData() {
    console.log('refreshSegmentData');
  }

  protected refreshData(): Observable<any> {
    console.log('refreshSegmentData');
    return of();
  }

  protected showLoader() {
    console.log('showLoader');
  }

  protected hideLoader() {
    console.log('hideLoader');
  }
}
